import { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const STAGES = [
  ["new", "New"],
  ["qualified", "Qualified"],
  ["nurture", "Nurture"],
  ["under_contract", "Under contract"],
  ["closed", "Closed"],
  ["lost", "Lost"],
];

const TYPES = [["buyer","Buyer"],["seller","Seller"],["both","Both"]];

/**
 * Edit modal for a Lead. Shows every editable field in one place so the agent
 * can fix a typo, update budget, or flip consent from one screen.
 *
 * Kept intentionally symmetrical with `ClientEdit` — the two forms
 * offer the same non-consent inputs so an agent moving a person between
 * pipelines never loses fidelity.
 */
export default function LeadEdit({ lead, onClose, onSaved }) {
  const [f, setF] = useState({
    first_name: lead.first_name || "",
    last_name: lead.last_name || "",
    email: lead.email || "",
    phone: lead.phone || "",
    address: lead.address || "",
    source: lead.source || "",
    stage: lead.stage,
    lead_type: lead.lead_type,
    property_interest: lead.property_interest || "",
    budget_min: lead.budget_min ?? "",
    budget_max: lead.budget_max ?? "",
    target_areas: lead.target_areas || "",
    timeline_date: lead.timeline_date || "",
    pre_approved: !!lead.pre_approved,
    lender_name: lead.lender_name || "",
    sms_consent: !!lead.sms_consent,
    email_consent: !!lead.email_consent,
    do_not_contact: !!lead.do_not_contact,
    notes: lead.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    // Require ONE of the name inputs. First+Last is the preferred path;
    // an existing lead with only full_name still passes because the
    // backend recomposes when either split field is provided.
    const composedName = [f.first_name, f.last_name].map(s => (s || "").trim()).filter(Boolean).join(" ");
    if (!composedName && !lead.full_name) return toast.error("Name is required");
    setSaving(true);
    try {
      const body = {
        ...f,
        // full_name is computed server-side from first/last, so we omit
        // it here to keep the split fields as the single source of truth.
        budget_min: f.budget_min === "" ? null : Number(f.budget_min),
        budget_max: f.budget_max === "" ? null : Number(f.budget_max),
        timeline_date: f.timeline_date || null,
        email: f.email || null,
        phone: f.phone || null,
        address: f.address || null,
        source: f.source || null,
        lender_name: f.lender_name || null,
        target_areas: f.target_areas || null,
        property_interest: f.property_interest || null,
        notes: f.notes || null,
      };
      await api.patch(`/leads/${lead.id}`, body);
      toast.success("Lead updated");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center px-4 py-8 overflow-y-auto" role="dialog" aria-label="Edit lead" data-testid="lead-edit-modal">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between px-6 pt-6 pb-3">
          <h2 className="font-heading text-xl font-bold tracking-tight text-slate-900">Edit lead</h2>
          <button onClick={onClose} className="p-2 rounded-md text-slate-400 hover:bg-slate-100" aria-label="Close" data-testid="lead-edit-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6 pt-0 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <F label="First name" required><input value={f.first_name} onChange={e=>setF({...f, first_name: e.target.value})} className="input" data-testid="lead-edit-first-name" /></F>
            <F label="Last name" required><input value={f.last_name} onChange={e=>setF({...f, last_name: e.target.value})} className="input" data-testid="lead-edit-last-name" /></F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Email"><input type="email" value={f.email} onChange={e=>setF({...f, email: e.target.value})} className="input" data-testid="lead-edit-email" /></F>
            <F label="Phone"><input value={f.phone} onChange={e=>setF({...f, phone: e.target.value})} className="input" data-testid="lead-edit-phone" /></F>
          </div>
          <F label="Address"><input value={f.address} onChange={e=>setF({...f, address: e.target.value})} placeholder="Home / mailing address" className="input" data-testid="lead-edit-address" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Stage">
              <select value={f.stage} onChange={e=>setF({...f, stage: e.target.value})} className="input" data-testid="lead-edit-stage">
                {STAGES.map(([v,l])=><option key={v} value={v}>{l}</option>)}
              </select>
            </F>
            <F label="Type">
              <select value={f.lead_type} onChange={e=>setF({...f, lead_type: e.target.value})} className="input" data-testid="lead-edit-type">
                {TYPES.map(([v,l])=><option key={v} value={v}>{l}</option>)}
              </select>
            </F>
          </div>
          <F label="Source"><input value={f.source} onChange={e=>setF({...f, source: e.target.value})} placeholder="Zillow, referral, open house…" className="input" data-testid="lead-edit-source" /></F>
          <F label="Property interest"><textarea value={f.property_interest} onChange={e=>setF({...f, property_interest: e.target.value})} rows={2} placeholder="Luxury single-family in Arcadia; pool required" className="input" data-testid="lead-edit-property-interest" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Price min"><input type="number" value={f.budget_min} onChange={e=>setF({...f, budget_min: e.target.value})} className="input" data-testid="lead-edit-budget-min" /></F>
            <F label="Price max"><input type="number" value={f.budget_max} onChange={e=>setF({...f, budget_max: e.target.value})} className="input" data-testid="lead-edit-budget-max" /></F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Target areas"><input value={f.target_areas} onChange={e=>setF({...f, target_areas: e.target.value})} placeholder="Gilbert, Chandler" className="input" /></F>
            <F label="Timeline"><input type="date" value={f.timeline_date} onChange={e=>setF({...f, timeline_date: e.target.value})} className="input" /></F>
          </div>
          <div className="grid grid-cols-2 gap-3 items-end">
            <F label="Lender"><input value={f.lender_name} onChange={e=>setF({...f, lender_name: e.target.value})} className="input" /></F>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 pb-2">
              <input type="checkbox" checked={f.pre_approved} onChange={e=>setF({...f, pre_approved: e.target.checked})} className="h-4 w-4 rounded border-slate-300" data-testid="lead-edit-preapproved" />
              Pre-approved
            </label>
          </div>
          <div className="rounded-md bg-slate-50 border border-slate-200 p-3 space-y-2 text-sm text-slate-700">
            <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500">Consent</div>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.sms_consent} onChange={e=>setF({...f, sms_consent: e.target.checked})} className="h-4 w-4" data-testid="lead-edit-sms" /> SMS consent on file</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.email_consent} onChange={e=>setF({...f, email_consent: e.target.checked})} className="h-4 w-4" data-testid="lead-edit-email-consent" /> Email consent on file</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.do_not_contact} onChange={e=>setF({...f, do_not_contact: e.target.checked})} className="h-4 w-4" data-testid="lead-edit-dnc" /> <span className="text-red-700 font-medium">Do not contact</span></label>
          </div>
          <F label="Notes"><textarea value={f.notes} onChange={e=>setF({...f, notes: e.target.value})} className="input min-h-[80px]" data-testid="lead-edit-notes" /></F>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button onClick={onClose} className="text-sm font-medium text-slate-600 hover:text-slate-900 px-3 py-2">Cancel</button>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-md px-5 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: "var(--primary-hex)" }} data-testid="lead-edit-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Save changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function F({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium uppercase tracking-widest text-slate-500 mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}
